import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { AgbChangeOwnershipRow, SupplyShedDriveTime } from '../../../lib/types';
import { usePrivateTimberlandCarbonMaintenance } from '../../../hooks/usePrivateTimberlandCarbonMaintenance';
import { tonsPerAcre } from '../../../lib/units';
import {
  CHART_AXIS_STROKE,
  CHART_AXIS_STYLE,
  CHART_PRIMARY_COLOR,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from '../../../lib/chartStyles';
import { FO_PALETTE } from '../../../lib/foColors';
import { AGB_CHANGE_PALETTE } from '../../../lib/agbChangeColors';
import { InsightProvenanceFooter } from './InsightProvenanceFooter';

interface Props {
  millCode: string | null;
  driveTime: SupplyShedDriveTime;
}

const formatAcres = (acres: number): string => {
  if (acres >= 1e6) return `${(acres / 1e6).toFixed(2)}M`;
  if (acres >= 1e3) return `${(acres / 1e3).toFixed(0)}K`;
  return acres.toFixed(0);
};

const formatPct = (frac: number): string => `${(frac * 100).toFixed(1)}%`;

const formatChange = (mgPerHa: number): string => {
  const sign = mgPerHa >= 0 ? '+' : '';
  return `${sign}${mgPerHa.toFixed(1)} Mg/ha`;
};

const yearWindowLabel = (years: readonly number[]): string =>
  years.length === 0 ? '' : `${years[0]}–${years[years.length - 1]}`;

interface CrosstabRowProps {
  label: string;
  swatch: string;
  row: AgbChangeOwnershipRow | null;
}

// Stacked horizontal bar split into loss (red) / stable (gray) / gain (green)
// share of the row's total acres. Reuses AGB_CHANGE_PALETTE so the bar reads
// against the same colors the map paints. Empty rows render as a thin
// neutral track so the layout doesn't collapse on zero-acre classes.
const CrosstabBar: React.FC<{ row: AgbChangeOwnershipRow }> = ({ row }) => {
  const total = row.totalAcres;
  if (total <= 0) {
    return <div className="h-2 rounded-sm bg-neutral-700/60" />;
  }
  const lossPct = (row.lossAcres / total) * 100;
  const stablePct = (row.stableAcres / total) * 100;
  const gainPct = (row.gainAcres / total) * 100;
  return (
    <div className="flex h-2 rounded-sm overflow-hidden">
      <div
        style={{ width: `${lossPct}%`, backgroundColor: AGB_CHANGE_PALETTE.colors.loss }}
        title={`Loss · ${formatAcres(row.lossAcres)} ac (${formatPct(row.lossAcres / total)})`}
      />
      <div
        style={{ width: `${stablePct}%`, backgroundColor: AGB_CHANGE_PALETTE.colors.stable }}
        title={`Stable · ${formatAcres(row.stableAcres)} ac (${formatPct(row.stableAcres / total)})`}
      />
      <div
        style={{ width: `${gainPct}%`, backgroundColor: AGB_CHANGE_PALETTE.colors.gain }}
        title={`Gain · ${formatAcres(row.gainAcres)} ac (${formatPct(row.gainAcres / total)})`}
      />
    </div>
  );
};

const CrosstabRow: React.FC<CrosstabRowProps> = ({ label, swatch, row }) => {
  if (!row || row.totalAcres <= 0) {
    return (
      <div className="grid grid-cols-[84px_56px_1fr_56px] sm:grid-cols-[110px_70px_1fr_70px] items-center gap-2 sm:gap-3 py-1.5">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: swatch }} />
          <p className="text-body-sm font-roboto text-neutral-200">{label}</p>
        </div>
        <p className="text-[11px] font-roboto-mono text-neutral-500">no acres</p>
        <div className="h-2 rounded-sm bg-neutral-700/60" />
        <p className="text-[11px] font-roboto-mono text-neutral-500 text-right">—</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[84px_56px_1fr_56px] sm:grid-cols-[110px_70px_1fr_70px] items-center gap-2 sm:gap-3 py-1.5">
      <div className="flex items-center gap-2">
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: swatch }} />
        <p className="text-body-sm font-roboto text-neutral-200">{label}</p>
      </div>
      <p className="text-[11px] font-roboto-mono text-neutral-400">
        {formatAcres(row.totalAcres)} ac
      </p>
      <CrosstabBar row={row} />
      <p
        className="text-[11px] font-roboto-mono font-bold text-right"
        style={{
          color:
            row.meanChangeMgPerHa >= 0
              ? AGB_CHANGE_PALETTE.colors.gain
              : AGB_CHANGE_PALETTE.colors.loss,
        }}
      >
        {formatChange(row.meanChangeMgPerHa)}
      </p>
    </div>
  );
};

export const PrivateTimberlandCarbonMaintenanceCard: React.FC<Props> = ({
  millCode,
  driveTime,
}) => {
  const { data, isLoading, error } = usePrivateTimberlandCarbonMaintenance(
    millCode,
    driveTime,
  );

  if (error) {
    return (
      <div className="bg-figma-card rounded-figma-panel p-4 border border-red-500/40 shadow-figma-card">
        <p className="text-xs font-roboto-mono text-red-400 uppercase tracking-wider">
          Insight unavailable
        </p>
        <p className="mt-2 text-body-sm font-roboto text-neutral-400">{error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
        <p className="text-xs font-roboto-mono text-neutral-500 uppercase tracking-wider">
          Private Timberland Carbon Maintenance
        </p>
        <p className="mt-3 text-body-sm font-roboto text-neutral-400">Computing…</p>
      </div>
    );
  }

  // Both fetches resolved without error but no `allPrivate` row was found —
  // i.e. the precompute returned zero family + corporate acres in this shed.
  // Distinct from the loading state so the card doesn't appear stuck.
  if (!data) {
    return (
      <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
        <p className="text-xs font-roboto-mono text-neutral-500 uppercase tracking-wider">
          Private Timberland Carbon Maintenance
        </p>
        <p className="mt-3 text-body-sm font-roboto text-neutral-400">
          No family- or corporate-owned timberland detected in this shed at the
          AGB-change product's resolution — the per-ownership maintenance metric
          isn't applicable here.
        </p>
      </div>
    );
  }

  const baselineLabel = yearWindowLabel(data.baselineYears);
  const latestLabel = yearWindowLabel(data.latestYears);
  const allPrivate = data.allPrivate;

  return (
    <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card space-y-4">
      <div>
        <p className="text-xs font-roboto-mono text-neutral-500 uppercase tracking-wider">
          Insight — Private Timberland Carbon Maintenance
        </p>
        <p
          className="mt-3 text-4xl font-roboto-mono font-bold leading-none"
          style={{
            color:
              allPrivate.meanChangeMgPerHa >= 0
                ? AGB_CHANGE_PALETTE.colors.gain
                : AGB_CHANGE_PALETTE.colors.loss,
          }}
        >
          {formatChange(allPrivate.meanChangeMgPerHa)}
        </p>
        <p className="mt-1 text-body-sm font-roboto text-neutral-300">
          mean AGB change on private timberland · {baselineLabel} → {latestLabel}
        </p>
      </div>

      <div className="space-y-1">
        <p className="text-[11px] font-roboto text-neutral-500 uppercase tracking-wider mb-1">
          Per ownership · gain / stable / loss share + mean change
        </p>
        <CrosstabRow
          label="Family"
          swatch={FO_PALETTE.colors.family}
          row={data.family}
        />
        <CrosstabRow
          label="Corporate"
          swatch={FO_PALETTE.colors.corporate}
          row={data.corporate}
        />
        <CrosstabRow
          label="All private"
          // Plain white on purpose — this is the family + corporate rollup row,
          // not a Forest Ownership class, so reusing FO_PALETTE.colors.* would
          // mis-suggest it's a sibling category.
          swatch="#FFFFFF"
          row={allPrivate}
        />
      </div>

      <div>
        <p className="text-xs font-roboto text-neutral-500 mb-1">
          Shed-mean AGB density · annual time series
        </p>
        <div className="h-24">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <LineChart data={data.sparkline} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="year"
                tick={{ ...CHART_AXIS_STYLE, fill: CHART_AXIS_STROKE }}
                axisLine={{ stroke: CHART_AXIS_STROKE }}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ ...CHART_AXIS_STYLE, fill: CHART_AXIS_STROKE }}
                axisLine={{ stroke: CHART_AXIS_STROKE }}
                tickLine={false}
                width={32}
                domain={['dataMin - 1', 'dataMax + 1']}
                tickFormatter={(value: number) => Math.round(value).toString()}
              />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                formatter={(value: number | undefined) => [
                  typeof value === 'number'
                    ? `${tonsPerAcre(value).toFixed(2)} tons/acre`
                    : '—',
                  'Density',
                ]}
              />
              <Line
                type="monotone"
                dataKey="avgDensity"
                stroke={CHART_PRIMARY_COLOR}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <InsightProvenanceFooter
        chips={[
          { color: AGB_CHANGE_PALETTE.colors.gain, label: 'AGB change · 100 m, smoothed endpoints' },
          { color: FO_PALETTE.colors.family, label: 'Forest Ownership · family + corporate (USFS RDS-2025-0045)' },
        ]}
        method={
          `Smoothed endpoint difference: mean(${latestLabel}) − mean(${baselineLabel}), per 100 m AGB pixel. ` +
          `Per-ownership rows attribute each AGB cell to its majority Forest Ownership class (downsampled from 10 m); ` +
          `gain / stable / loss share is the row's pixel count rolled up from the diverging 5-bin classification ` +
          `( ${'<'} -20 / -20 to -5 / -5 to +5 / +5 to +20 / ${'>'} +20 Mg/ha ). ` +
          `The Forest Ownership raster reports broad ownership type at 10 m and does not encode certification status — ` +
          `SFI- or FSC-certified acres are a subset of these private classes that this layer cannot identify.`
        }
      />
    </div>
  );
};
